import React from "react";
import clsx from "clsx";

import PlaybookScriptService from "../../services/PlaybookScriptService";

const TEXT_HEAD_CLASS = "tk-table__head-cell";
const AMOUNT_HEAD_CLASS = "tk-table__head-cell tk-table__cell--end";
const TEXT_CELL_CLASS = "tk-table__truncate";
const AMOUNT_CELL_CLASS = "tk-table__numeric tk-table__cell--end";
const MISSING_SIDE_TEXT = "—";
const ACTIONS_TITLE = "What it asks Workflow to do";

function PlaybookScriptResults({ results, regression, manifest }) {
	if (results) {
		const { sections, columns, actions } = PlaybookScriptService.describeScriptResults({ results, manifest });
		const headClasses = columns.map(column => (column.isAmount ? AMOUNT_HEAD_CLASS : TEXT_HEAD_CLASS));
		const cellClasses = columns.map(column => (column.isAmount ? AMOUNT_CELL_CLASS : TEXT_CELL_CLASS));
		const exceptionSections = sections.filter(section => section.isException);
		const rowSections = sections.filter(section => !section.isException);
		const hasActions = actions.length > 0;
		const renderSection = (section, sectionIndex) => (
			<section
				key={`${section.title}-${sectionIndex}`}
				className={clsx("playbook-script-results__section", {
					"playbook-script-results__section--exception": section.isException && section.rows.length > 0,
				})}
			>
				<div className="playbook-script-results__title">
					{section.title}
					<span className="playbook-script-results__count">{section.rows.length}</span>
				</div>
				{section.rows.length === 0 && <div className="tk-empty tk-card playbook-script-results__empty">{section.isException ? "None." : "No rows."}</div>}
				{section.rows.length > 0 && (
					<div className="tk-table tk-table--dense tk-card playbook-script-results__table">
						<div className="tk-table__row tk-table__row--head">
							{columns.map((column, columnIndex) => (
								<span key={column.field} className={headClasses[columnIndex]}>
									{column.field}
								</span>
							))}
						</div>
						{section.rows.map(row => (
							<div key={row.key} className="tk-table__row tk-table__row--body">
								{row.cells.map((cell, cellIndex) => (
									<span key={columns[cellIndex].field} className={cellClasses[cellIndex]}>
										{cell}
									</span>
								))}
							</div>
						))}
					</div>
				)}
			</section>
		);
		return (
			<div className="playbook-script-results">
				{exceptionSections.map(renderSection)}
				{hasActions && (
					<section className="playbook-script-results__section">
						<div className="playbook-script-results__title">
							{ACTIONS_TITLE}
							<span className="playbook-script-results__count">{actions.length}</span>
						</div>
						<div className="tk-table tk-table--dense tk-card playbook-script-results__table playbook-script-results__table--actions">
							<div className="tk-table__row tk-table__row--head">
								<span className={TEXT_HEAD_CLASS}>Action</span>
								<span className={TEXT_HEAD_CLASS}>Confidence</span>
								<span className={TEXT_HEAD_CLASS}>Reason</span>
							</div>
							{actions.map(action => (
								<div key={action.key} className="tk-table__row tk-table__row--body">
									<span className="playbook-script-results__action-title">{action.title}</span>
									<span className={TEXT_CELL_CLASS}>{action.confidence}</span>
									<span className="playbook-script-results__action-reason">{action.reason}</span>
								</div>
							))}
						</div>
					</section>
				)}
				{rowSections.map(renderSection)}
			</div>
		);
	}

	const differences = PlaybookScriptService.describeScriptDifferences({ regression, manifest });
	if (differences.isHidden || differences.failureText) return null;
	const hasRowDifferences = differences.rows.length > 0;
	const hasActionLines = differences.actionLines.length > 0;
	if (!hasRowDifferences && !hasActionLines) return <div className="tk-empty tk-card playbook-script-results__empty">No differences.</div>;

	const { columns } = differences;
	const headClasses = columns.map(column => (column.isAmount ? AMOUNT_HEAD_CLASS : TEXT_HEAD_CLASS));
	const cellClasses = columns.map(column => (column.isAmount ? AMOUNT_CELL_CLASS : TEXT_CELL_CLASS));
	return (
		<div className="playbook-script-results">
			{hasRowDifferences && (
				<div className="tk-table tk-table--dense tk-card playbook-script-results__table playbook-script-results__table--differences">
					<div className="tk-table__row tk-table__row--head">
						<span className={TEXT_HEAD_CLASS}>List</span>
						<span className={TEXT_HEAD_CLASS}>Change</span>
						<span className={TEXT_HEAD_CLASS} aria-hidden="true" />
						{columns.map((column, columnIndex) => (
							<span key={column.field} className={headClasses[columnIndex]}>
								{column.field}
							</span>
						))}
					</div>
					{differences.rows.map(difference => (
						<div key={difference.key} className="playbook-script-results__pair">
							<div className="tk-table__row tk-table__row--body playbook-script-results__side">
								<span className="tk-table__truncate">{difference.listTitle}</span>
								<span className="playbook-script-results__change">{difference.changeText}</span>
								<span className="playbook-script-results__side-label">Before</span>
								{!difference.before && <span className="tk-table__faint">{MISSING_SIDE_TEXT}</span>}
								{difference.before &&
									columns.map((column, cellIndex) => (
										<span key={column.field} className={cellClasses[cellIndex]}>
											{difference.before[cellIndex]}
										</span>
									))}
							</div>
							<div className="tk-table__row tk-table__row--body playbook-script-results__side playbook-script-results__side--after">
								<span aria-hidden="true" />
								<span aria-hidden="true" />
								<span className="playbook-script-results__side-label">After</span>
								{!difference.after && <span className="tk-table__faint">{MISSING_SIDE_TEXT}</span>}
								{difference.after &&
									columns.map((column, cellIndex) => {
										const isChangedCell = Boolean(difference.before) && difference.before[cellIndex] !== difference.after[cellIndex];
										const afterCellClass = clsx(cellClasses[cellIndex], { "playbook-script-results__cell--changed": isChangedCell });
										return (
											<span key={column.field} className={afterCellClass}>
												{difference.after[cellIndex]}
											</span>
										);
									})}
							</div>
						</div>
					))}
				</div>
			)}
			{hasActionLines && (
				<ul className="tk-card playbook-script-results__action-lines">
					{differences.actionLines.map(actionLine => (
						<li key={actionLine.key} className="playbook-script-results__action-line">
							<span className="playbook-script-results__change">{actionLine.changeText}</span>
							<span className="playbook-script-results__action-what">
								<span className="playbook-script-results__action-title">{actionLine.text}</span>
								{actionLine.details && <span className="playbook-script-results__action-details">{actionLine.details}</span>}
							</span>
							<span className="playbook-script-results__action-reason">{actionLine.reason}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export default PlaybookScriptResults;
